import React, { useCallback, useEffect, useRef, useState } from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useUpdateEffect } from "react-use";
import { Button, Checkbox, CloseIcon, IconButton, Label, Menu, InputTime } from "@activecollab/components";
import isEqual from "lodash/isEqual";
import moment from "moment";
import PropTypes from "prop-types";
import * as yup from "yup";
import { getPersonalizedTimeFormat } from "~/store/selectors/selector.configOptions";
import { CalendarEventDateTrigger } from "./CalendarEventDateTrigger";
import useAcSelectDate from "../Hooks/useAcSelectDate";
import SelectDateNonWorkingDays from "../SelectDate/SelectDateNonWorkingDays";
import { showErrorToast } from "../Toast";

const validator = yup.object().shape({
  start_date: yup.number().required(),
  end_date: yup.number().required(),
  is_all_day: yup.bool(),
  start_time: yup.string().when("is_all_day", {
    is: false,
    then: yup.string().required(),
    otherwise: yup.string().trim(),
  }),
  end_time: yup
    .string()
    .when("is_all_day", {
      is: false,
      then: yup.string().required(),
      otherwise: yup.string().trim(),
    })
    .test("is-greater", "end time should be greater", function (value) {
      const { start_date, end_date, start_time, is_all_day } = this.parent;

      if (!value || !start_time) {
        return false;
      }
      if (is_all_day) {
        return true;
      }
      let start = moment.unix(start_date);
      let [hours_start, minutes_start] = start_time.split(":");
      start = start.set({
        hours: Number(hours_start),
        minutes: Number(minutes_start),
        seconds: 0,
      });
      let end = moment.unix(end_date);
      let [hours_end, minutes_end] = value.split(":");
      end = end.set({
        hours: Number(hours_end),
        minutes: Number(minutes_end),
        seconds: 0,
      });

      return end.isAfter(start);
    }),
});

const makeTime = (time) => {
  if (typeof time === "number") {
    return moment.unix(time).format("HH:mm");
  }
  return null;
};

export const CalendarEventSelectDate = ({
  startsOn: _startsOn,
  startsOnTime,
  endsOn: _endsOn,
  endsOnTime,
  onSave,
  canEdit,
}) => {
  const startsOn = _startsOn;
  const endsOn = _endsOn;

  const { t } = useTranslation();
  const formatTime = useSelector(getPersonalizedTimeFormat);
  const [open, setOpen] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const [errors, setErrors] = useState({});

  const { dates, handleDateChange } = useAcSelectDate({
    from: startsOnTime === null ? _startsOn : moment.utc(moment.unix(_startsOn).format("YYYY-MM-DD")).unix(),
    to: endsOnTime === null ? _endsOn : moment.utc(moment.unix(_endsOn).format("YYYY-MM-DD")).unix(),
  });

  const makeValues = () => {
    return {
      start_date: _startsOn,
      end_date: _endsOn,
      start_time: makeTime(_startsOn),
      end_time: makeTime(_endsOn),
      is_all_day: startsOnTime === null && endsOnTime === null,
    };
  };

  const [values, setValues] = useState(makeValues);
  const initialValues = useRef(makeValues()).current;

  const { is_all_day: isAllDay } = values;

  const [dirty, setDirty] = useState(false);

  const minEndsOn = useMemo(() => {
    if (moment.unix(dates.from).isSame(moment.unix(dates.to), "day") && values.start_time && values.end_time) {
      let start = moment(values.start_time, "HH:mm");
      return start.add(15, "minutes").format("HH:mm");
    } else {
      return "00:00";
    }
  }, [dates, values]);

  const handleTrigger = useCallback(() => {
    setOpen(!open);
  }, [open]);

  const setDefaultTime = useCallback(() => {
    let result = {};
    if (startsOnTime) {
      result = {
        ...result,
        start_time: moment.unix(startsOn).format("HH:mm"),
      };
    } else {
      result = {
        ...result,
        start_time: "09:00",
      };
    }

    if (endsOnTime) {
      result = {
        ...result,
        end_time: moment.unix(endsOn).format("HH:mm"),
      };
    } else {
      result = {
        ...result,
        end_time: "10:00",
      };
    }
    setValues((prevState) => {
      return {
        ...prevState,
        ...result,
      };
    });
  }, [endsOn, endsOnTime, startsOn, startsOnTime]);

  const handleChangeCheckbox = useCallback(
    async (event) => {
      if (!event.target.checked) {
        setDefaultTime();
      }

      setValues((prevState) => {
        return {
          ...prevState,
          is_all_day: !prevState.is_all_day,
        };
      });
    },
    [setDefaultTime]
  );

  const handleSave = useCallback(() => {
    const _startTime = values.start_time;
    const _endTime = values.end_time;
    const makeAllDay = _startTime === "00:00:00" && _endTime === "00:00:00";
    const from = moment.utc(moment.unix(dates.from)).format("YYYY-MM-DD");
    let to = moment.utc(moment.unix(dates.to ? dates.to : dates.from)).format("YYYY-MM-DD");

    if (makeAllDay) {
      to = moment
        .utc(moment.unix(dates.to ? dates.to : dates.from))
        .add(-1, "day")
        .format("YYYY-MM-DD");
    }

    if (typeof onSave === "function") {
      onSave(from, to, makeAllDay || isAllDay ? null : _startTime, makeAllDay || isAllDay ? null : _endTime).then(
        (result) => {
          if (result?.error) {
            showErrorToast(t("Unable to save changes."));
            setOpen(true);
          } else {
            setOpen(false);
          }
        }
      );
    }
  }, [dates.from, dates.to, isAllDay, onSave, t, values.end_time, values.start_time]);

  const handleStartOnTimeChange = useCallback(async (timeString) => {
    setValues((prevState) => {
      return {
        ...prevState,
        start_time: timeString,
      };
    });
  }, []);

  const handleEndsOnTimeChange = useCallback(async (timeString) => {
    setValues((prevState) => {
      return {
        ...prevState,
        end_time: timeString,
      };
    });
  }, []);

  const onDateChange = useCallback(
    async (value) => {
      setValues((prevState) => {
        return {
          ...prevState,
          start_date: value.from,
          end_date: value.to === null ? value.from : value.to,
        };
      });
      handleDateChange(value);
    },
    [handleDateChange]
  );

  useEffect(() => {
    if (startsOn && endsOn) {
      handleDateChange({
        from: startsOnTime === null ? _startsOn : moment.utc(moment.unix(_startsOn).format("YYYY-MM-DD")).unix(),
        to: endsOnTime === null ? _endsOn : moment.utc(moment.unix(_endsOn).format("YYYY-MM-DD")).unix(),
      });
    }
  }, [_endsOn, _startsOn, endsOn, endsOnTime, handleDateChange, setDefaultTime, startsOn, startsOnTime]);

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => {
    handleDateChange({
      from: startsOnTime === null ? _startsOn : moment.utc(moment.unix(_startsOn).format("YYYY-MM-DD")).unix(),
      to: endsOnTime === null ? _endsOn : moment.utc(moment.unix(_endsOn).format("YYYY-MM-DD")).unix(),
    });
    setOpen(false);
  }, [_endsOn, _startsOn, endsOnTime, handleDateChange, startsOnTime]);

  useUpdateEffect(() => {
    validator.isValid(values).then((res) => {
      setDisabled(!res);
    });
  }, [values]);

  useEffect(() => {
    setDirty(!isEqual(initialValues, values));
  }, [initialValues, values]);

  useEffect(() => {
    validator
      .validate(values)
      .then(() => {
        setErrors({});
      })
      .catch((err) => {
        setErrors((prevState) => {
          return {
            ...prevState,
            [err.path]: err.errors.join(" "),
          };
        });
      });
  }, [values]);

  return (
    <Menu
      className="tw-w-[262px]"
      onOpen={handleOpen}
      open={open}
      position="bottom-start"
      onClose={handleClose}
      target={
        <CalendarEventDateTrigger
          startsOn={startsOn}
          startsOnTime={startsOnTime}
          endsOn={endsOn}
          endsOnTime={endsOnTime}
          onClick={handleTrigger}
          disabled={!canEdit}
        />
      }
    >
      <div className="tw-w-full tw-bg-page-paper-main">
        <SelectDateNonWorkingDays
          trigger="inline"
          selectedDays={dates}
          required
          onSave={onDateChange}
        />
        <div className="tw-flex tw-flex-col tw-gap-[14px] tw-px-[16px] tw-py-[20px] tw-border-0 tw-border-t tw-border-b tw-border-solid tw-border-border-primary">
          <div className="tw-flex tw-flex-row tw-items-center">
            <Checkbox
              onChange={handleChangeCheckbox}
              name="allDay"
              data-testid="allDay"
              className="tw-mr-2"
              checked={values.is_all_day}
              id="allDay"
            />
            <Label
              htmlFor="allDay"
              size="regular"
              weight="regular"
              className="tw-cursor-pointer"
            >
              {t("All day event")}
            </Label>
          </div>
          {isAllDay ? null : (
            <div className="tw-flex tw-gap-1">
              <div className="tw-flex tw-flex-col tw-gap-1 tw-w-full">
                <InputTime
                  size="small"
                  className="tw-flex-1 tw-max-w-full"
                  selected={values.start_time}
                  min="00:00"
                  max="23:45"
                  onChange={handleStartOnTimeChange}
                  mode={formatTime === "HH:mm" ? "24" : "12"}
                  invalid={errors.start_time}
                  placeholder={values.start_time}
                  addCustomTimeLabel={t("Add")}
                />
                <InputTime
                  size="small"
                  className="tw-flex-1 tw-max-w-full"
                  selected={values.end_time}
                  min={minEndsOn}
                  max="23:45"
                  onChange={handleEndsOnTimeChange}
                  mode={formatTime === "HH:mm" ? "24" : "12"}
                  invalid={errors.end_time}
                  placeholder={values.end_time}
                  addCustomTimeLabel={t("Add")}
                />
              </div>
            </div>
          )}
        </div>
        <div className="tw-flex tw-gap-4 tw-px-6 tw-py-3 tw-items-center">
          <Button
            size="small"
            onClick={handleSave}
            disabled={disabled || !dirty}
          >
            {t("Save")}
          </Button>

          <IconButton
            variant="option"
            size="small"
            type="button"
            active={false}
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
        </div>
      </div>
    </Menu>
  );
};
CalendarEventSelectDate.propTypes = {
  startsOn: PropTypes.number,
  startsOnTime: PropTypes.string,
  endsOn: PropTypes.number,
  endsOnTime: PropTypes.string,
  onSave: PropTypes.func,
  canEdit: PropTypes.bool,
};

CalendarEventSelectDate.defaultProps = {
  startsOn: moment().unix(),
  endsOn: moment().unix(),
  startsOnTime: null,
  endsOnTime: null,
};
